/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import type React from 'react';
import { useRef } from 'react';

import { findFocusableChildren, findFocusableSibling } from '../_common/utils';
import { DragIcon } from '../icons';
import {
  focusCellInNextRow,
  focusCellInPreviousRow,
  focusNextRow,
  focusPreviousRow,
} from './tree-view-keyboard-helpers';
import { type TreeItem, type TreeItemComponentProps } from './tree-view-types';
import { Trail } from './TreeViewTrail';

/**
 * Handles keydown events for when the item itself is focused (wrapperRef.current)
 * Moves the focus between the item and its children as well as toggles collapsing of the item
 * @param event
 * @param shouldDisableInteraction
 * @param wrapperRef
 * @param onCollapse
 * @param item
 * @returns
 */

const onWrapperKeyDown = (
  event: React.KeyboardEvent,
  shouldDisableInteraction: boolean,
  wrapperRef: React.RefObject<HTMLElement | null>,
  onCollapse: (() => void) | undefined,
  item: TreeItem<Record<string, unknown>>,
) => {
  if (shouldDisableInteraction || wrapperRef.current === null) {
    return;
  }

  if (event.key === 'Enter' && onCollapse) {
    onCollapse();
  } else if (event.key === 'ArrowRight') {
    event.preventDefault();
    if (item.canHaveSubItems && item.isCollapsed && onCollapse) {
      onCollapse();
    } else {
      const focusable = findFocusableChildren(wrapperRef.current);
      const [firstElement] = focusable;
      if (firstElement instanceof HTMLElement) {
        firstElement.focus();
      }
    }

    event.preventDefault();
  } else if (event.key === 'ArrowLeft') {
    event.preventDefault();
    if (item.canHaveSubItems && !item.isCollapsed && onCollapse) {
      onCollapse();
    } else {
      focusPreviousRow(wrapperRef.current);
    }
  } else if (event.key === 'ArrowDown') {
    event.preventDefault();
    focusNextRow(wrapperRef.current);
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    focusPreviousRow(wrapperRef.current);
  }
};

/**
 * Handles keydown events for when one of the items children is focused
 * Moves the focus between the children of the item and up to the parent if needed.
 * Stops the event from propagating to the parent since it has it own keydown handler
 * The parent is the wrapperRef.current and uses onWrapperKeyDown
 * @param event
 * @param shouldDisableInteraction
 * @param wrapperRef
 */
const onInnerDivKeyDown = (
  event: React.KeyboardEvent,
  shouldDisableInteraction: boolean,
  wrapperRef: React.RefObject<HTMLElement | null>,
) => {
  if (shouldDisableInteraction) {
    return;
  }
  event.stopPropagation();
  if (event.key === 'ArrowRight') {
    event.preventDefault();
    const nextElement = findFocusableSibling(wrapperRef, 'next');
    if (nextElement) {
      nextElement.focus();
    }
  } else if (event.key === 'ArrowLeft') {
    event.preventDefault();
    const previousElement = findFocusableSibling(wrapperRef, 'prev');
    if (previousElement) {
      previousElement.focus();
    } else {
      wrapperRef.current?.focus();
    }
  } else if (event.key === 'ArrowDown' && wrapperRef.current !== null) {
    event.preventDefault();
    focusCellInNextRow(wrapperRef.current);
  } else if (event.key === 'ArrowUp' && wrapperRef.current !== null) {
    event.preventDefault();
    focusCellInPreviousRow(wrapperRef.current);
  } else if (event.shiftKey && event.key === 'Tab') {
    const previousElement = findFocusableSibling(wrapperRef, 'prev');
    if (previousElement) {
      event.preventDefault();
      previousElement.focus();
    }
  } else if (event.key === 'Tab') {
    if (event.shiftKey) {
      return;
    }

    const nextElement = findFocusableSibling(wrapperRef, 'next');
    if (nextElement) {
      nextElement.focus();
      event.preventDefault();
    }
  }
};

/**
 * Used as a helper function to wrap the TreeItem component
 * This adds the drag handle and trail to the start of a tree item.
 * Useful if you want to add additional functionality to the tree item but still keep the drag handle and trail
 * Is also used internally to create the TreeViewTextItem component
 */
function TreeItemWrapperInner<T extends Record<string, unknown>>({
  depth,
  shouldDisableInteraction,
  shouldDisableSorting,
  isGhost,
  isIndicator,
  onCollapse,
  item,
  setNodeRef,
  setActivatorNodeRef,
  dragHandleProps,
  style,
  className,
  trails,
  children,
  indentationWidth,
  ariaLevel,
  ariaPosInSet,
  ariaSetSize,
  tabIndex,
  onFocus,
  ref,
  // Need to destruct to avoid passing it to the component
  // oxlint-disable-next-line no-unused-vars
  ...restProps
}: React.PropsWithChildren<TreeItemComponentProps<T>>) {
  const wrapperRef = useRef<HTMLElement | null>(null);

  return (
    <li
      ref={(el) => {
        wrapperRef.current = el;

        if (el === null) {
          return;
        }
        setNodeRef(el);
      }}
      className={classNames({
        className,
        indicator: isIndicator,
        'ndl-tree-view-list-item': true,
        'ndl-tree-view-list-item-disable-interaction': shouldDisableInteraction,
        'ndl-tree-view-list-item-placeholder': isGhost,
      })}
      style={{
        ...style,
        paddingLeft: isIndicator ? `${depth * indentationWidth}px` : 0,
      }}
      role="treeitem"
      onFocus={onFocus}
      aria-level={ariaLevel}
      aria-posinset={ariaPosInSet}
      aria-setsize={ariaSetSize}
      aria-selected={undefined}
      aria-checked={item.isSelected && !item.isSkeletonLoading}
      aria-expanded={item.canHaveSubItems ? !item.isCollapsed : undefined}
      onKeyDown={(event) =>
        onWrapperKeyDown(
          event,
          shouldDisableInteraction ?? false,
          wrapperRef,
          onCollapse,
          item,
        )
      }
    >
      <div
        role="presentation"
        className="ndl-tree-view-list-item-content"
        ref={ref}
        onKeyDown={(event) =>
          onInnerDivKeyDown(
            event,
            shouldDisableInteraction ?? false,
            wrapperRef,
          )
        }
      >
        {!shouldDisableSorting && (
          <button
            type="button"
            className="ndl-tree-view-drag-handle"
            ref={setActivatorNodeRef}
            aria-label="Pick up"
            {...dragHandleProps}
            tabIndex={tabIndex}
          >
            <DragIcon />
          </button>
        )}
        {trails.map((trail, index) => {
          return <Trail key={index} variant={trail} />;
        })}

        {children}
      </div>
    </li>
  );
}

export const TreeItemWrapper = TreeItemWrapperInner;
